/**
 * @fileoverview Página de resultados de um fluxo (/fluxo/:id/resultados): nome,
 * descrição e estado, indicadores do período (Entradas | Enviadas | Entregues |
 * Cliques | Conversões), entradas por dia e o histórico de execuções. O editor
 * visual continua em /fluxo/:id e abre pelo botão "Editar fluxo".
 *
 * Só administradores veem os resultados (o histórico mostra contatos e
 * conversas): para os demais o servidor responde 403 e a tela oferece o editor.
 * Com a regra nova ligada, quem vê os fluxos (flows.view) vê os resultados, e a
 * descrição, a medição e o editor pedem flows.edit (sem ela, o fluxo abre só
 * para ver).
 * Os indicadores se atualizam sozinhos a cada minuto enquanto a aba está
 * visível; o histórico só recarrega pelo botão "Atualizar", para não perder as
 * páginas já abertas.
 *
 * Depois que a página carregou, uma falha ao trocar o período ou ao atualizar
 * vira um aviso e a tela continua com o que já mostrava (o histórico aberto, os
 * filtros e o painel da execução não se perdem).
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Dialog } from "@material-ui/core";
import { ArrowLeft, Eye, Pencil, RefreshCw, SlidersHorizontal } from "lucide-react";
import { toast } from "react-toastify";
import MainContainer from "../../../components/MainContainer";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { i18n } from "../../../translate/i18n";
import usePermission from "../../../hooks/usePermission";
import useStyles from "./styles";
import MetricCards, { MetricCardsSkeleton } from "./MetricCards";
import EntriesChart, { EntriesChartSkeleton } from "./EntriesChart";
import ExecutionsHistory from "./ExecutionsHistory";
import ExecutionDrawer from "./ExecutionDrawer";
import MeasurementDialog from "./MeasurementDialog";
import { formatDayLong } from "./format";

const PRESETS = ["7d", "30d", "90d"];
const AUTO_REFRESH_MS = 60 * 1000;
const MAX_DESCRIPTION = 500;
const t = (key, options) => i18n.t(`flowResults.${key}`, options);

const DescriptionDialog = ({ classes, open, initial, onClose, onSave }) => {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setText(initial || "");
  }, [open, initial]);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(text);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth classes={{ paper: classes.dialogPaper }}>
      <div className={classes.dialogTitle}>{t("descriptionLabel")}</div>
      <div className={classes.dialogBody}>
        <textarea
          className={classes.textArea}
          value={text}
          maxLength={MAX_DESCRIPTION}
          placeholder={t("descriptionPlaceholder")}
          onChange={(e) => setText(e.target.value)}
          aria-label={t("descriptionLabel")}
          autoFocus
        />
        <div className={classes.counter}>
          {text.length}/{MAX_DESCRIPTION}
        </div>
      </div>
      <div className={classes.dialogActions}>
        <button type="button" className={classes.secondaryBtn} onClick={onClose} disabled={saving}>
          {t("cancel")}
        </button>
        <button type="button" className={classes.primaryBtn} onClick={save} disabled={saving}>
          {t("save")}
        </button>
      </div>
    </Dialog>
  );
};

const HeaderSkeleton = ({ classes }) => (
  <div aria-hidden="true" style={{ marginBottom: 20 }}>
    <div className={classes.skeleton} style={{ width: 260, height: 26 }} />
    <div className={classes.skeleton} style={{ width: 380, maxWidth: "80%", height: 13, marginTop: 10 }} />
  </div>
);

const FlowResults = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const canEdit = usePermission().can("flows.edit", true);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState({ period: "30d" });
  const [customOpen, setCustomOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [measureOpen, setMeasureOpen] = useState(false);
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  // Ignora respostas antigas (período trocado no meio do caminho).
  const requestSeq = useRef(0);
  const loadedRef = useRef(false);
  const errorRef = useRef(null);
  errorRef.current = error;

  const executionId = Number(new URLSearchParams(location.search).get("execucao")) || null;

  // "load": primeira carga ou troca de período (esqueleto nos indicadores);
  // "refresh": botão Atualizar (botão ocupado, erro em aviso);
  // "quiet": atualização automática (uma falha passageira é ignorada).
  const fetchResults = useCallback(
    async (mode = "load") => {
      const seq = ++requestSeq.current;
      if (mode === "load") setLoading(true);
      if (mode === "refresh") setRefreshing(true);
      try {
        const { data: results } = await api.get(`/workflows/${id}/results`, { params: period });
        if (seq !== requestSeq.current) return;
        setData(results);
        setError(null);
        loadedRef.current = true;
      } catch (err) {
        if (seq !== requestSeq.current) return;
        const status = err.response?.status;
        // Sem acesso ou fluxo apagado: a página deixa de valer, em qualquer modo.
        if (status === 403 || status === 404) setError(status === 403 ? "forbidden" : "notFound");
        else if (mode === "quiet") return;
        else if (loadedRef.current) toastError(err);
        else setError("load");
      } finally {
        if (seq === requestSeq.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [id, period]
  );

  useEffect(() => {
    fetchResults("load");
  }, [fetchResults]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === "visible" && !errorRef.current) fetchResults("quiet");
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchResults]);

  // As datas do "Personalizado" acompanham o intervalo que o servidor usou (ele
  // corta o que passa de um ano e o que cai no futuro).
  const shown = data?.period;
  const shownPreset = shown?.preset;
  const shownFrom = shown?.from;
  const shownTo = shown?.to;
  useEffect(() => {
    if (shownPreset !== "custom") return;
    setCustomFrom(shownFrom);
    setCustomTo(shownTo);
  }, [shownPreset, shownFrom, shownTo]);

  const refresh = () => {
    fetchResults("refresh");
    setRefreshKey((key) => key + 1);
  };

  // Abrir empilha uma entrada no histórico do navegador; fechar volta para ela.
  // Assim o botão Voltar fecha o painel uma vez, em vez de reabrir os painéis
  // já fechados. Quem chegou por um link direto não tem para onde voltar: troca.
  const openExecution = (executionIdToOpen) => {
    const params = new URLSearchParams(location.search);
    if (executionIdToOpen) {
      params.set("execucao", executionIdToOpen);
      history.push({ pathname: location.pathname, search: `?${params.toString()}`, state: { drawer: true } });
      return;
    }
    if (location.state?.drawer) {
      history.goBack();
      return;
    }
    params.delete("execucao");
    const query = params.toString();
    history.replace({ pathname: location.pathname, search: query ? `?${query}` : "" });
  };

  const saveDescription = async (description) => {
    await api.put(`/workflows/${id}/settings`, { description });
    toast.success(t("saved"));
    await fetchResults("quiet");
  };

  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    setPeriod({ period: "custom", from: customFrom, to: customTo });
  };

  const openEditor = () => history.push(`/fluxo/${id}`, { from: "results" });
  // O botão marcado é o período que os números na tela mostram (durante a
  // troca, o que foi pedido). Abrir as datas do "Personalizado" sem aplicar não
  // muda o que está marcado.
  const activePreset = loading || !shown ? period.period : shown.preset;

  return (
    <MainContainer>
      <div className={classes.page}>
        <button type="button" className={classes.backLink} onClick={() => history.push("/fluxo")}>
          <ArrowLeft size={14} /> {t("back")}
        </button>

        {error && (
          <div className={`${classes.banner} ${error === "forbidden" ? classes.bannerNeutral : classes.bannerError}`} role="alert">
            <span style={{ flex: 1 }}>{t(`errors.${error}`)}</span>
            {error === "load" && (
              <button type="button" className={`${classes.secondaryBtn} ${classes.smallBtn}`} onClick={() => fetchResults("load")}>
                {t("errors.retry")}
              </button>
            )}
            {error === "forbidden" && (
              <button type="button" className={`${classes.secondaryBtn} ${classes.smallBtn}`} onClick={openEditor}>
                {t("openEditor")}
              </button>
            )}
          </div>
        )}

        {!error && !data && (
          <>
            <HeaderSkeleton classes={classes} />
            <MetricCardsSkeleton classes={classes} />
            <div className={classes.section}>
              <EntriesChartSkeleton classes={classes} />
            </div>
          </>
        )}

        {!error && data && (
          <>
            <div className={classes.header}>
              <div className={classes.headerMain}>
                <div className={classes.titleRow}>
                  <div className={classes.title}>{data.workflow.title}</div>
                  <span className={classes.statusChip}>
                    <span className={data.workflow.isActive ? classes.dotOk : classes.dotOff} />
                    {t(data.workflow.isActive ? "active" : "inactive")}
                  </span>
                </div>
                <div className={`${classes.description} ${data.workflow.description ? "" : classes.descriptionEmpty}`}>
                  {data.workflow.description || t("noDescription")}
                  {canEdit && (
                    <button type="button" className={classes.textBtn} onClick={() => setDescriptionOpen(true)}>
                      {t(data.workflow.description ? "editDescription" : "addDescription")}
                    </button>
                  )}
                </div>
                <div className={classes.since}>
                  {data.trackingSince ? t("trackingSince", { date: formatDayLong(data.trackingSince) }) : t("notTrackedYet")}
                </div>
              </div>
              <div className={classes.headerActions}>
                {canEdit && (
                  <button type="button" className={classes.secondaryBtn} onClick={() => setMeasureOpen(true)}>
                    <SlidersHorizontal size={15} /> {t("measure.button")}
                  </button>
                )}
                <button type="button" className={classes.primaryBtn} onClick={openEditor}>
                  {canEdit ? <Pencil size={15} /> : <Eye size={15} />} {t(canEdit ? "edit" : "view")}
                </button>
              </div>
            </div>

            {data.enabled === false && <div className={`${classes.banner} ${classes.bannerNeutral}`}>{t("disabled")}</div>}

            <div className={classes.toolbar}>
              <div className={classes.segmented} role="group" aria-label={t("period.label")}>
                {PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    className={`${classes.segment} ${activePreset === preset ? classes.segmentActive : ""}`}
                    aria-pressed={activePreset === preset}
                    onClick={() => {
                      setCustomOpen(false);
                      setPeriod({ period: preset });
                    }}
                  >
                    {t(`period.${preset}`)}
                  </button>
                ))}
                <button
                  type="button"
                  className={`${classes.segment} ${activePreset === "custom" ? classes.segmentActive : ""}`}
                  aria-pressed={activePreset === "custom"}
                  aria-expanded={customOpen}
                  onClick={() => {
                    setCustomOpen((isOpen) => !isOpen || activePreset === "custom");
                    setCustomFrom((value) => value || data.period.from);
                    setCustomTo((value) => value || data.period.to);
                  }}
                >
                  {t("period.custom")}
                </button>
              </div>
              {customOpen && (
                <div className={classes.dateRange}>
                  <input
                    type="date"
                    className={classes.dateInput}
                    value={customFrom}
                    max={customTo || undefined}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    aria-label={t("period.from")}
                  />
                  {t("period.to")}
                  <input
                    type="date"
                    className={classes.dateInput}
                    value={customTo}
                    min={customFrom || undefined}
                    onChange={(e) => setCustomTo(e.target.value)}
                    aria-label={t("period.to")}
                  />
                  <button
                    type="button"
                    className={`${classes.secondaryBtn} ${classes.smallBtn}`}
                    onClick={applyCustom}
                    disabled={!customFrom || !customTo}
                  >
                    {t("period.apply")}
                  </button>
                </div>
              )}
              <div className={classes.toolbarRight}>
                {t("period.range", { from: formatDayLong(data.period.from), to: formatDayLong(data.period.to) })}
                <button type="button" className={`${classes.secondaryBtn} ${classes.smallBtn}`} onClick={refresh} disabled={loading || refreshing}>
                  <RefreshCw size={13} /> {t(refreshing ? "refreshing" : "refresh")}
                </button>
              </div>
            </div>

            {loading ? (
              <MetricCardsSkeleton classes={classes} />
            ) : (
              <MetricCards classes={classes} data={data} onConfigure={() => setMeasureOpen(true)} />
            )}

            <div className={classes.section}>
              <div className={classes.sectionHeader}>
                <div className={classes.sectionTitle}>{t("chart.title")}</div>
                <div className={classes.sectionHint}>{t("chart.hint")}</div>
              </div>
              {loading ? <EntriesChartSkeleton classes={classes} /> : <EntriesChart classes={classes} series={data.series} />}
            </div>

            <ExecutionsHistory
              classes={classes}
              workflowId={id}
              kinds={data.workflow.kinds}
              period={data.period}
              refreshKey={refreshKey}
              onOpen={openExecution}
            />

            <ExecutionDrawer classes={classes} workflowId={id} executionId={executionId} onClose={() => openExecution(null)} />
            <MeasurementDialog
              classes={classes}
              open={measureOpen}
              onClose={() => setMeasureOpen(false)}
              workflowId={id}
              data={data}
              onSaved={() => fetchResults("quiet")}
            />
            <DescriptionDialog
              classes={classes}
              open={descriptionOpen}
              initial={data.workflow.description}
              onClose={() => setDescriptionOpen(false)}
              onSave={saveDescription}
            />
          </>
        )}
      </div>
    </MainContainer>
  );
};

export default FlowResults;
